<template>
  <content-layout>
    <template #header>Guide</template>
    <template #description>
      Each article is its own page at <b>lavishlocations.com/guide/&lt;web address&gt;</b>, and is listed on the
      guide page and in the website's "Location owners" menu. The one marked <b>Locations Wanted page</b> appears on
      lavishlocations.com/locationswanted instead.
    </template>
    <template #header-right>
      <el-button type="primary" icon="el-icon-plus" @click="sectionDrawer=true">Add article</el-button>
    </template>

    <el-form v-if="info" :model="info" label-position="top">
      <el-form-item label="Guide page title">
        <div class="inline-field">
          <el-input v-model="info.title"></el-input>
          <el-button type="primary" @click="saveInfo('Information Saved.')">Save</el-button>
        </div>
      </el-form-item>
    </el-form>

    <h2 class="section-title">Articles</h2>
    <p class="help-text">Click an article to edit it. Drag articles to change the order; the
      new order saves straight away.</p>

    <div v-if="info" class="item-list">
      <draggable v-model="info.sections" @start="drag=true" @end="drag=false, saveInfo('Order Changed')">
        <div v-for="section in info.sections" :key="section._id" @click="sectionDrawer=true, activeSection=section"
             class="item-list__row">
          <i class="el-icon-rank item-list__handle"></i>
          <span class="item-list__title">
            {{ section.title }}
            <el-tag v-if="section.pageKey === 'locationswanted'" size="mini" type="warning">Locations Wanted page</el-tag>
            <span v-else class="item-list__meta">/guide/{{ section.slug }}</span>
          </span>
          <span class="item-list__action">Edit</span>
        </div>
      </draggable>
    </div>

    <el-drawer
      :title="activeSection._id ? 'Edit article' : 'New article'"
      :visible.sync="sectionDrawer"
      :direction="direction"
      :size="'60%'"
      @close="activeSection = {title: '', icon: '', image: {}}"
    >
      <!--SECTION FORM-->
      <el-form :model="activeSection" label-position="top">
        <el-form-item label="Title">
          <el-input v-model="activeSection.title" @blur="fillSlug(activeSection)"></el-input>
        </el-form-item>

        <el-form-item label="Show on">
          <el-radio-group :value="activeSection.pageKey === 'locationswanted' ? 'locationswanted' : 'guide'"
                          @input="setPageKey">
            <el-radio-button label="guide">Guide article</el-radio-button>
            <el-radio-button label="locationswanted">Locations Wanted page</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="activeSection.pageKey !== 'locationswanted'" label="Web address">
          <el-input v-model="activeSection.slug" @blur="activeSection.slug = slugify(activeSection.slug || activeSection.title)">
            <template slot="prepend">lavishlocations.com/guide/</template>
          </el-input>
          <div class="field-help">Filled in from the title. Changing it later breaks existing links to this article.</div>
        </el-form-item>

        <el-form-item label="Search description">
          <el-input type="textarea" :autosize="{minRows: 3, maxRows: 6}" maxlength="160" show-word-limit v-model="activeSection.metaDescription"
                    placeholder="One or two sentences shown in Google results. Leave blank to use the start of the article."></el-input>
        </el-form-item>

        <el-form-item label="Icon">
          <el-select
            v-model="activeSection.icon"
            filterable
            default-first-option
            placeholder="Choose an Icon">
            <el-option
              v-for="icon in icons"
              :key="icon.iconName"
              :label="icon.iconName"
              :value="icon.iconName">
              <span><font-awesome-icon :icon="icon.iconName"/>  {{icon.iconName}}</span>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item v-if="activeSection._id" label="Image">
          <el-upload
            :action="rootpath +'/api/image'"
            :headers="headers"
            :file-list="fileList"
            name="image"
            list-type="picture-card"
            :multiple="false"
            :limit="1"
            :on-success="handleImageUpload"
            :on-remove="handleImageRemove">
            <i class="el-icon-plus"></i>
          </el-upload>
        </el-form-item>

        <div class="form-actions">
          <!--If no active section id then create button else save button-->
          <el-button v-if="!activeSection._id" type="primary" @click="createSection">Create</el-button>
          <el-button v-if="activeSection._id" type="primary" @click="saveSection">Save</el-button>
          <el-button v-if="activeSection._id" class="form-actions__right" type="danger" plain icon="el-icon-delete"
                     @click="deleteItem('section')">Delete article</el-button>
        </div>
      </el-form>

      <!--Only show content if active section already has been saved-->
      <div v-if="activeSection._id" class="content-blocks">
        <div class="content-blocks__heading">
          <h2 class="section-title">Content</h2>
          <el-button size="small" icon="el-icon-plus" @click="contentDrawer = true">Add content block</el-button>
        </div>
        <p class="help-text">The article's text, in blocks. Each block's title is a heading on the page.</p>

        <div class="item-list">
          <draggable v-model="activeSection.content" @start="drag=true"
                     @end="drag=false, saveInfo('Order Changed', false, false, false, false)">
            <div v-for="content in activeSection.content" :key="content._id"
                 @click="contentDrawer=true, activeContent=content"
                 class="item-list__row">
              <i class="el-icon-rank item-list__handle"></i>
              <span v-if="content.title" class="item-list__title">{{ content.title }}</span>
              <span v-else class="item-list__title item-list__empty-title">No title (introduction)</span>
              <span class="item-list__action">Edit</span>
            </div>
          </draggable>
        </div>
      </div>
    </el-drawer>

    <el-drawer
      :title="activeContent._id ? 'Edit content block' : 'New content block'"
      :append-to-body="true"
      :visible.sync="contentDrawer"
      :size="'50%'"
      @close="activeContent = {title: '', description: ''}">
      <!--CONTENT FORM-->
      <el-form :model="activeContent" label-position="top">
        <el-form-item label="Title">
          <el-input v-model="activeContent.title" placeholder="Optional. Shown as a heading."></el-input>
        </el-form-item>

        <el-form-item label="Text">
          <div class="rich-text">
            <ckeditor :editor="editor" v-model="activeContent.description" :config="editorConfig"></ckeditor>
          </div>
        </el-form-item>

        <div class="form-actions">
          <!--If no active content id then create button else save button-->
          <el-button v-if="!activeContent._id" type="primary"
                     @click="saveInfo('Content Created',false, true, false, true)">Create
          </el-button>
          <el-button v-if="activeContent._id" type="primary"
                     @click="saveInfo('Content Saved', false, false, false, true)">Save
          </el-button>
          <el-button v-if="activeContent._id" class="form-actions__right" type="danger" plain icon="el-icon-delete"
                     @click="deleteItem('content')">Delete block</el-button>
        </div>
      </el-form>
    </el-drawer>
  </content-layout>
</template>

<script>
  // We import fas here so we can create our picker. See if you can access it globally instead.
  import {fas} from '@fortawesome/free-solid-svg-icons'
  import ClassicEditor from '@ckeditor/ckeditor5-build-classic';


  export default {
    name: "Information",
    data() {
      return {
        activeSection: {
          title: '',
          icon: '',
          image: {
            url: '',
            fileName: ''
          }
        },
        activeContent: {
          title: '',
          description: ''
        },
        info: '',
        sectionDrawer: false,
        contentDrawer: false,
        direction: 'rtl',
        icons: fas,
        editor: ClassicEditor,
        editorData: '<p>Content of the editor.</p>',
        editorConfig: {

        },
        rootpath: process.env.VUE_APP_ROOT_API,
        headers: {
          Authorization: localStorage.getItem('Authorization')
        },
      }
    },

    methods: {

      // Keep in step with website/utilityData/guide.js, so a saved slug matches the address the
      // website was already using. "The do's and don'ts" -> "the-dos-and-donts"
      slugify(title) {
        return (title || '')
          .toLowerCase()
          .replace(/&/g, ' and ')
          .replace(/['’]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      },

      fillSlug(section) {
        if (!section.slug && section.title) {
          this.$set(section, 'slug', this.slugify(section.title));
        }
      },

      // Sections saved before slugs and page keys existed: give them the address the website
      // already uses, and tag the last one as Locations Wanted (the old rule). Saved with the
      // next change.
      prepareGuide(info) {
        if (!info || !info.sections) return info;
        info.sections.forEach(section => this.fillSlug(section));
        const tagged = info.sections.some(section => section.pageKey === 'locationswanted');
        if (!tagged && info.sections.length) {
          this.$set(info.sections[info.sections.length - 1], 'pageKey', 'locationswanted');
        }
        return info;
      },

      // Only one section can be the Locations Wanted page.
      setPageKey(value) {
        if (value === 'locationswanted') {
          (this.info.sections || []).forEach(section => {
            if (section !== this.activeSection) this.$set(section, 'pageKey', undefined);
          });
        }
        this.$set(this.activeSection, 'pageKey', value === 'locationswanted' ? 'locationswanted' : undefined);
        this.fillSlug(this.activeSection);
      },

      // START API METHODS

      getInfo() {
        return this.axios.get(`${process.env.VUE_APP_ROOT_API}/api/infoPage`, {headers: this.headers});
      },

      patchInfo() {
        return this.axios.patch(`${process.env.VUE_APP_ROOT_API}/api/infoPage/${this.info._id}`, this.info, {headers: this.headers});
      },

      deleteImage() {
        return this.axios.delete(`${process.env.VUE_APP_ROOT_API}/api/image/${this.activeSection.image.fileName}`, {headers: this.headers});
      },

      // END API METHODS

      createSection() {

        this.fillSlug(this.activeSection);
        this.info.sections.push(this.activeSection);
        this.patchInfo()
          .then(response => {
            this.$notify({title: 'Success', message: 'Section Created', type: 'success'});
            this.sectionDrawer = false;
            return this.getInfo();
          })
          .then(response => {
            this.info = this.prepareGuide(response.data[0]);
          })
          .catch(err => {
            this.$notify({title: 'Error', message: err, type: 'warning'});
          });

      },

      saveSection() {

        this.patchInfo()
          .then(response => {
            this.$notify({title: 'Success', message: 'Section Saved', type: 'success'});
            this.sectionDrawer = false;
            return this.getInfo();
          })
          .then(response => {
            this.info = this.prepareGuide(response.data[0]);
          })
          .catch(err => {
            this.$notify({title: 'Error', message: err, type: 'warning'});
          });

      },

      createContent() {

        this.activeSection.content.push(this.activeContent);
        this.patchInfo()
          .then(response => {

          })
          .catch(err => {
            this.$notify({title: 'Error', message: err, type: 'warning'});
          });
      },

      saveContent() {

      },

      saveInfo(successMessage, newSection = false, newContent = false, closeSection = true, closeContent = true) {

        //If new section then push it to array of sections before saving.
        if (newSection) {
          this.info.sections.push(this.activeSection);
        } else if (newContent) {
          this.activeSection.content.push(this.activeContent);
        }

        this.patchInfo().then(response => {
          this.$notify({title: 'Success', message: successMessage, type: 'success'});
          if (closeSection) {
            this.sectionDrawer = false;
          }
          if (closeContent) {
            this.contentDrawer = false;
          }
          return this.getInfo();
        }).then(response => {
          this.info = this.prepareGuide(response.data[0]);
          // Sketchy code to update the active section.
          if (this.activeSection._id) {
            this.activeSection = this.info.sections.find(section => {
              return section._id == this.activeSection._id;
            });
          }
        })
          .catch(error => {
            this.$notify({title: 'Error', message: error, type: 'warning'});
          });

      },

      deleteItem(type) {
        this.$confirm(`This will permanently delete the ${type} and all its contents. Continue?`, 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          if (type == 'section') {
            this.info.sections.splice(this.info.sections.findIndex(section => section._id === this.activeSection._id), 1);
            this.sectionDrawer = false;
          } else if (type == 'content') {
            this.activeSection.content.splice(this.activeSection.content.findIndex(content => content._id === this.activeContent._id), 1);
            this.contentDrawer = false;
          }
          this.saveInfo(`${type} deleted.`, false, false, false);

        }).catch(() => {

        });
      },

      handleImageRemove() {

        this.deleteImage().then(response => {

          this.activeSection.image = {url: '', fileName: ''};
          return this.patchInfo();

        }).then(response => {

          this.$notify({title: 'Success', message: 'Image Deleted', type: 'success'});
          this.getInfo();

        }).catch(err => {
          this.$notify({title: 'Error', message: err, type: 'warning'});
        });

      },

      handleImageUpload(response, file, fileList) {
        fileList = [];
        this.activeSection.image.url = response.url;
        this.activeSection.image.fileName = response.fileName;
      }


    },

    computed: {
      fileList: function () {
        if (this.activeSection.image.fileName) {
          return [{name: this.activeSection.image.fileName, url: this.activeSection.image.url}];
        } else {
          return [];
        }
      }
    },

    // START LIFECYCLE HOOKS

    beforeMount() {

      this.getInfo().then(response => {
        this.info = this.prepareGuide(response.data[0]);
      }).catch(err => {
        console.log(err);
      });

    },


    // END LIFECYCLE HOOKS


  }
</script>

<style>
  /* CKEditor's link/image pop-ups have to sit above the drawers. */
  .ck-balloon-panel {
    z-index: 5000 !important;
  }
</style>

<style scoped>
  .inline-field {
    display: flex;
    gap: 12px;
    max-width: 640px;
  }

  .field-help {
    margin-top: 4px;
    color: var(--text-muted);
    font-size: 12px;
    line-height: 18px;
  }

  .content-blocks {
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid var(--border);
  }

  .content-blocks__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 820px;
  }

  .content-blocks__heading .section-title {
    margin: 0;
  }

  .rich-text {
    width: 100%;
  }

  .rich-text >>> .ck-editor__editable {
    min-height: 320px;
  }
</style>



// WEBPACK FOOTER //
// src/site-content/Information.vue